<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIndicator,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "reka-ui";
import { computed } from "vue";
import { useSelector } from "@xstate/vue";

import router from "@/router";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { authMachine } from "@/machines/auth-machine";

import { useBrandStore } from "@/stores/brand-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

const isRefreshing = useSelector(authMachine, (snapshot) => snapshot.matches({ authenticated: "refreshPermissions" }));

const brands = computed(() => {
  const { accessibleBrands, selectedBrand } = useBrandStore();

  return {
    accessible: accessibleBrands,
    get current(): string | undefined {
      return selectedBrand;
    },
    set current(brand: string) {
      router.push({ name: "brand", params: { brand } });
    },
  };
});

const userName = computed(() => useUserPermissionStore().userName);
</script>
<template>
  <DropdownMenuRoot v-if="userName != null">
    <DropdownMenuTrigger
      aria-label="User Details"
      class="tw-h-[1.875rem] tw-w-[2.671875rem] tw-rounded-md tw-bg-gray-300 tw-text-gray-800"
    >
      <StringTooltip side="right" item="User Details">
        <div class="tw-flex tw-size-full tw-items-center tw-justify-center">
          <i :aria-hidden="true" class="fa fa-user-gear fa-lg"></i>
        </div>
      </StringTooltip>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        side="top"
        align="start"
        :side-offset="10"
        class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
      >
        <DropdownMenuItem class="tw-rounded-md tw-px-2 tw-py-1 tw-outline-none" @select.prevent>
          Logged in as {{ userName }}
        </DropdownMenuItem>
        <DropdownMenuSeparator class="!tw-my-1 tw-h-px tw-w-full tw-bg-gray-500" />
        <!-- <DropdownMenuItem :as-child="true" @select.prevent>
              <a
                target="_blank"
                rel="noopener noreferrer"
                :href="`${config.ssoUrl}/profiles/${user.tenant}/${user.product}/${user.identityProviderId}`"
                class="tw-py-2 tw-px-4 tw-rounded-md tw-items-center tw-justify-start tw-cursor-pointer hover:tw-bg-gray-400 tw-outline-none tw-flex tw-gap-2"
              >
                <i class="fas fa-arrow-up-right-from-square"></i>
                View Profile
              </a>
            </DropdownMenuItem> -->
        <template v-if="brands.current != null">
          <DropdownMenuSub v-if="brands.accessible.length > 1">
            <DropdownMenuSubTrigger
              class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
            >
              Switch Brand ({{ brands.current.toUpperCase() }})
              <div aria-hidden="true" class="tw-flex-1"></div>
              <i aria-hidden="true" class="fas fa-chevron-right"></i>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                side="right"
                align="start"
                :side-offset="15"
                class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
              >
                <DropdownMenuRadioGroup v-model="brands.current">
                  <DropdownMenuRadioItem
                    v-for="brand in brands.accessible"
                    :key="brand"
                    :value="brand"
                    class="tw-grid tw-cursor-pointer tw-grid-cols-[1rem_minmax(0,1fr)] tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                  >
                    <DropdownMenuItemIndicator>
                      <i class="fas fa-check fa-sm"></i>
                    </DropdownMenuItemIndicator>
                    <span class="tw-col-start-2">
                      {{ brand.toUpperCase() }}
                    </span>
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem
            v-else-if="brands.current !== ''"
            class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
            @select.prevent
          >
            Brand: {{ brands.current.toUpperCase() }}
          </DropdownMenuItem>
          <DropdownMenuItem
            class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
            @select.prevent
            @click="() => authMachine.send({ type: 'REFRESH_PERMISSIONS' })"
          >
            <i class="fas fa-refresh" :class="{ 'fa-spin': isRefreshing }"></i>
            Refresh Permissions
          </DropdownMenuItem>
        </template>
        <DropdownMenuSeparator class="!tw-my-1 tw-h-px tw-w-full tw-bg-gray-500" />
        <DropdownMenuItem
          class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
          @select.prevent
          @click="() => authMachine.send({ type: 'LOGOUT', everywhere: false })"
        >
          <i class="fas fa-sign-out"></i>
          Logout
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
          >
            <!-- <i class="fas fa-sign-out"></i> -->
            Logout Everywhere
            <div aria-hidden="true" class="tw-flex-1"></div>
            <i aria-hidden="true" class="fas fa-chevron-right"></i>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              side="right"
              align="start"
              :side-offset="15"
              class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
            >
              <DropdownMenuItem
                class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                @select.prevent
                @click="() => authMachine.send({ type: 'LOGOUT', everywhere: true })"
              >
                Click to confirm
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
